import {useQuotaBannerImpression} from '@github-ui/copilot-chat/components/quota/use-quota-banner-impression'
import {
  ENABLE_ADDITIONAL_REQUESTS_URL,
  UPGRADE_FREEZE_LEARN_MORE_URL,
  UPGRADE_PLAN_URL,
} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {CopilotPlan} from '@github-ui/copilot-chat-types'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Banner, Link, LinkButton} from '@primer/react'
import {type ReactNode, useEffect, useState} from 'react'

import styles from './QuotaExceededBanner.module.css'

export function useQuotaExceededBannerVisibility() {
  const {
    isLicensedLimited,
    chatQuotaRemaining,
    premiumInteractionsQuotaExceeded,
    overagesEnabled,
    tokenBasedBillingEnabled,
  } = useEntitlement()
  const [isVisible, setIsVisible] = useState<boolean>(() => {
    return copilotLocalStorage.getQuotaExceededBannerFlag() ?? false
  })

  useEffect(() => {
    if (chatQuotaRemaining !== undefined) {
      const existingFlag = copilotLocalStorage.getQuotaExceededBannerFlag()

      if (!overagesEnabled && premiumInteractionsQuotaExceeded) {
        if (existingFlag === null) {
          copilotLocalStorage.setQuotaExceededBannerFlag(true)
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setIsVisible(true)
        } else {
          setIsVisible(existingFlag)
        }
      } else {
        copilotLocalStorage.removeQuotaExceededBannerFlag()
        setIsVisible(false)
      }
    }
  }, [chatQuotaRemaining, overagesEnabled, premiumInteractionsQuotaExceeded])

  const handleDismiss = () => {
    copilotLocalStorage.setQuotaExceededBannerFlag(false)
    setIsVisible(false)
  }

  return {
    visible: isVisible && !isLicensedLimited && chatQuotaRemaining !== undefined && !tokenBasedBillingEnabled,
    onDismiss: handleDismiss,
  }
}

interface QuotaExceededBannerProps {
  onDismiss: () => void
}

export function QuotaExceededBanner({onDismiss}: QuotaExceededBannerProps) {
  const {plan, canUpgradePlan, resetDate} = useEntitlement()
  useQuotaBannerImpression({variant: 'quota_exceeded', source: 'copilot-immersive-v1'})

  let description: ReactNode = null
  let primaryAction: ReactNode = null

  if (plan === CopilotPlan.IndividualFree) {
    description = copilotFeatureFlags.upgradeFreezeEnabled
      ? `You have used all requests available this month. Limit resets on ${resetDate}.`
      : `You have used all requests available this month. Upgrade your plan to increase your limit. Limit resets on ${resetDate}.`
    if (copilotFeatureFlags.upgradeFreezeEnabled) {
      description = (
        <>
          {description} Plan upgrades are temporarily unavailable.{' '}
          <Link inline href={UPGRADE_FREEZE_LEARN_MORE_URL}>
            Read more
          </Link>
        </>
      )
    } else {
      primaryAction = (
        <LinkButton
          as="a"
          href={UPGRADE_PLAN_URL}
          onClick={() => {
            sendEvent('dotcom_chat.activate', {
              target: 'QUOTA_BANNER_EXCEEDED',
              action: 'upgrade',
              mode: 'immersive',
              tokenBasedBillingEnabled: false,
              plan,
            })
          }}
        >
          Upgrade
        </LinkButton>
      )
    }
  } else if (
    plan === CopilotPlan.IndividualPro ||
    plan === CopilotPlan.IndividualProPlus ||
    plan === CopilotPlan.IndividualStudent ||
    plan === CopilotPlan.IndividualMax
  ) {
    if (canUpgradePlan) {
      description = copilotFeatureFlags.upgradeFreezeEnabled
        ? `You have used all premium requests available this month. Limit resets on ${resetDate}.`
        : `You have used all premium requests available this month. Upgrade your plan to increase your limit. Limit resets on ${resetDate}.`
      if (copilotFeatureFlags.upgradeFreezeEnabled) {
        description = (
          <>
            {description} Plan upgrades are temporarily unavailable.{' '}
            <Link inline href={UPGRADE_FREEZE_LEARN_MORE_URL}>
              Read more
            </Link>
          </>
        )
      } else {
        primaryAction = (
          <LinkButton
            as="a"
            href={UPGRADE_PLAN_URL}
            onClick={() => {
              sendEvent('dotcom_chat.activate', {
                target: 'QUOTA_BANNER_EXCEEDED',
                action: 'upgrade',
                mode: 'immersive',
                tokenBasedBillingEnabled: false,
                plan,
              })
            }}
          >
            Upgrade
          </LinkButton>
        )
      }
    } else {
      description = `You have used all premium requests available this month. Enable additional requests to get more. Limit resets on ${resetDate}.`
      primaryAction = (
        <LinkButton
          as="a"
          href={ENABLE_ADDITIONAL_REQUESTS_URL}
          onClick={() => {
            sendEvent('dotcom_chat.activate', {
              target: 'QUOTA_BANNER_EXCEEDED',
              action: 'overages',
              mode: 'immersive',
              tokenBasedBillingEnabled: false,
              plan,
            })
          }}
        >
          Enable additional requests
        </LinkButton>
      )
    }
  } else if (plan === CopilotPlan.Business || plan === CopilotPlan.Enterprise) {
    description = `You have reached your monthly limit for premium requests. Ask your admin to increase your limit. Limit resets on ${resetDate}.`
  }

  return (
    <div className={styles.container}>
      <Banner
        variant="upsell"
        title="Limit reached"
        description={description}
        hideTitle
        primaryAction={primaryAction}
        onDismiss={() => {
          onDismiss()
          sendEvent('dotcom_chat.activate', {
            target: 'QUOTA_BANNER_EXCEEDED',
            action: 'dismiss',
            mode: 'immersive',
            tokenBasedBillingEnabled: false,
            plan,
          })
        }}
      />
    </div>
  )
}
